// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/homeState.ts
// 🔧 PURPOSE: CHE HOME MOSTRARE, dalle sole date della prenotazione e
//             dall'ora di Roma. Una funzione pura (niente React, niente
//             rete): la Home la chiama a ogni minuto e i test la provano
//             giorno per giorno.
//
//             Quattro viste:
//             - guest  (G): nessuna prenotazione in sessione;
//             - before (B): prima dell'arrivo, e il giorno dell'arrivo fino
//               all'ora di check-in (la variante "OGGI");
//             - stay   (S): dal giorno dell'arrivo all'ora di check-in (quella
//               del channel manager, di norma 15:00: l'ospite è in
//               casa: WiFi, cibo e bevande) fino al giorno della partenza;
//             - after  (A): dal giorno dopo la partenza, finché dura la
//               sessione (recensione, "prenota di nuovo"). La casa non si
//               comanda più: niente codice, WiFi, clima, lavatrice. Anzi, già
//               dall'orario di check-out più un'ora (stayOver), come il
//               channel manager.
//
//             Le fasi sono quelle di stayTiming (lontano, ultimi 7 giorni,
//             arrivo, soggiorno, partenza, dopo), ma sui giorni di ROMA: le
//             date arrivano "AAAA-MM-GG" da /booking/info e in ISO UTC da
//             chat/auth, e una prenotazione salvata alla mezzanotte di Roma
//             in UTC è ancora "il giorno prima". Si portano entrambe al
//             giorno di Roma, come "oggi".
//
//             La riga dell'intestazione conta le NOTTI, come Esplora, il
//             televisore e il tablet in casa (lib/stayNight): il giorno
//             dell'arrivo è "Notte 1 di 7", la vigilia "Ultima notte", il
//             giorno della partenza "Giorno della partenza"; con una notte
//             sola "Una notte con noi · partenza domani" (stayDayLabel).
//
//             Date mancanti o senza notti: vista S senza contatori (come la
//             vecchia Home senza fase), ma sempre A dopo la partenza.
// ============================================

import { CHECKIN_CONFIG } from '../config/checkin';
import type { CheckInRedo, ExtrasCatalog, GuestBooking } from './guestApi';
import {
  DEFAULT_CHECK_IN_TIME,
  DEFAULT_CHECK_OUT_TIME,
  resolveCheckInTime,
  resolveCheckOutTime,
  resolveHouseCheckInTime,
  timeMinutes,
} from './houseTimes';
import { dayKeyDiff, romeDayKey, romeHour, romeMinutes, shiftDayKey, stayOverAt } from './romeDay';
import { stayTiming, type HomePhase } from './stayPhase';
import { stayNightOf } from './stayNight';

export type HomeView = 'guest' | 'before' | 'stay' | 'after';

/**
 * Gli orari di ripiego (lib/houseTimes): valgono solo quando il channel
 * manager non manda i suoi. Il giorno dell'arrivo la Home passa al
 * soggiorno all'ora di check-in vera (state.checkInTime), non alle 15 fisse.
 */
export { DEFAULT_CHECK_IN_TIME, DEFAULT_CHECK_OUT_TIME };
/** Gli extra che portano dall'aeroporto o dalla stazione: già presi, niente taxi da proporre. */
export const TRANSFER_IDS = ['airport_transfer', 'station_transfer'];

export type Greeting = 'morning' | 'afternoon' | 'evening';

/** Quello che serve della prenotazione (tutto facoltativo: i test ne danno poco). */
export type HomeBooking = Partial<Pick<
  GuestBooking,
  | 'checkIn'
  | 'checkOut'
  | 'onlineCheckInCompleted'
  | 'isGroupBooking'
  | 'touristTax'
  | 'customCheckOutTime'
  | 'checkInTime'
  | 'checkOutTime'
  | 'purchasedExtras'
  | 'checkInRedo'
>>;

export interface HomeStateInput {
  /** La prenotazione della sessione; null/undefined = ospite non entrato. */
  booking?: HomeBooking | null;
  /** Il catalogo degli extra, se già arrivato: orario di uscita vero, taxi già preso. */
  catalog?: Pick<ExtrasCatalog, 'purchasedExtras' | 'lateCheckout' | 'customCheckOutTime' | 'checkOutTime' | 'checkInTime'> | null;
}

export interface HomeState {
  view: HomeView;
  /** La fase di stayTiming sui giorni di Roma; null senza date valide o senza notti. */
  phase: HomePhase | null;
  /** Oggi a Roma ("AAAA-MM-GG"). */
  today: string;
  /** Giorno di arrivo e di partenza, di Roma ("AAAA-MM-GG"); null se mancano. */
  checkInDay: string | null;
  checkOutDay: string | null;
  /** Giorni all'arrivo (negativo dopo) e alla partenza; null senza contatori. */
  daysToCheckIn: number | null;
  daysToCheckOut: number | null;
  totalNights: number | null;
  /**
   * La notte del soggiorno (1 = quella del giorno dell'arrivo), dall'arrivo
   * alla vigilia della partenza; il giorno della partenza e fuori dal
   * soggiorno null.
   */
  night: number | null;
  isArrivalDay: boolean;
  /** Il giorno dell'arrivo dall'ora di check-in: l'ospite è (o può essere) in casa. */
  pastCheckInTime: boolean;
  /** La vigilia della partenza: l'ultimo giorno intero (fase stay, domani si parte). */
  isEve: boolean;
  isCheckoutDay: boolean;
  /** Prima della mezzanotte di Roma del giorno di arrivo. */
  preArrival: boolean;
  /** Dal giorno dopo la partenza: la vista A. */
  stayEnded: boolean;
  /**
   * Il giorno della partenza oltre l'orario di check-out più un'ora, e dopo:
   * la casa non si comanda più (niente codice, WiFi, clima, lavatrice), come
   * per il channel manager. La vista resta S fino a mezzanotte.
   */
  stayOver: boolean;
  checkInDone: boolean;
  /**
   * Il gestore ha rifiutato il check-in e chiede di rifarlo (motivo e
   * nota). Solo finche' il check-in non e' rifatto; si puo' rifare fino
   * alla partenza, anche fuori dalla finestra di sempre.
   */
  checkInRedo: CheckInRedo | null;
  isGroup: boolean;
  /** Il check-in online dei gruppi ha la sua pagina. */
  checkInPath: '/check-in' | '/group-check-in';
  /** Il check-in online si può fare oggi (7 giorni prima, fino al giorno dopo l'arrivo). */
  checkInWindowOpen: boolean;
  /** Il giorno in cui si apre ("AAAA-MM-GG"), se non è ancora aperto; altrimenti null. */
  checkInOpensOn: string | null;
  /** Imposta di soggiorno chiesta e non pagata (anche prima dell'arrivo). */
  taxDue: boolean;
  /** L'orario di uscita vero: late check-out comprato, poi quello del channel manager, 10:00. */
  checkoutTime: string;
  /** L'ora da cui si entra: check-in anticipato comprato, poi quella del channel manager, 15:00. */
  checkInTime: string;
  /** L'ora di check-in della struttura (senza l'anticipo): per l'offerta "Entra prima delle …". */
  houseCheckInTime: string;
  /** Un transfer (aeroporto o stazione) è già stato preso. */
  transferBooked: boolean;
  /** Il codice della porta si può chiedere (con la verifica dell'email). */
  doorCodeAvailable: boolean;
  /** WiFi, clima e lavatrice: dal giorno di arrivo a quello della partenza, a check-in fatto. */
  homeAvailable: boolean;
  /** Il saluto dell'intestazione, dall'ora di Roma. */
  greeting: Greeting;
}

/** "AAAA-MM-GG" resta com'è (è già il giorno di Roma); un ISO si porta al giorno di Roma. */
export function bookingDayKey(value: string | null | undefined): string | null {
  if (!value) return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return romeDayKey(s) || null;
}

/** Mezzanotte locale del giorno "AAAA-MM-GG": così stayTiming conta i giorni di Roma. */
const localMidnight = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export function greetingFor(hour: number): Greeting {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

export function deriveHomeState(session: HomeStateInput | null | undefined, now: Date = new Date()): HomeState {
  const today = romeDayKey(now);
  const hour = romeHour(now);
  const booking = session?.booking || null;
  const catalog = session?.catalog || null;

  const base: HomeState = {
    view: 'guest',
    phase: null,
    today,
    checkInDay: null,
    checkOutDay: null,
    daysToCheckIn: null,
    daysToCheckOut: null,
    totalNights: null,
    night: null,
    isArrivalDay: false,
    pastCheckInTime: false,
    isEve: false,
    isCheckoutDay: false,
    preArrival: false,
    stayEnded: false,
    stayOver: false,
    checkInDone: false,
    checkInRedo: null,
    isGroup: false,
    checkInPath: '/check-in',
    checkInWindowOpen: false,
    checkInOpensOn: null,
    taxDue: false,
    checkoutTime: DEFAULT_CHECK_OUT_TIME,
    checkInTime: DEFAULT_CHECK_IN_TIME,
    houseCheckInTime: DEFAULT_CHECK_IN_TIME,
    transferBooked: false,
    doorCodeAvailable: false,
    homeAvailable: false,
    greeting: greetingFor(hour),
  };

  if (!booking) return base;

  // ---- Quello che non dipende dalle date ----
  const checkInDone = !!booking.onlineCheckInCompleted;
  const isGroup = !!booking.isGroupBooking;
  const purchased = catalog?.purchasedExtras || booking.purchasedExtras || [];
  // Gli orari da una fonte sola (lib/houseTimes): quelli del channel manager.
  const times = { booking, catalog };
  const checkoutTime = resolveCheckOutTime(times);
  const checkInTime = resolveCheckInTime(times);

  const state: HomeState = {
    ...base,
    view: 'stay',
    checkInDone,
    checkInRedo: !checkInDone && booking.checkInRedo ? booking.checkInRedo : null,
    isGroup,
    checkInPath: isGroup ? '/group-check-in' : '/check-in',
    taxDue: booking.touristTax?.status === 'requested',
    checkoutTime,
    checkInTime,
    houseCheckInTime: resolveHouseCheckInTime(times),
    transferBooked: purchased.some((e) => TRANSFER_IDS.indexOf(e.serviceId) >= 0),
  };

  // ---- Le date, sui giorni di Roma ----
  const checkInDay = bookingDayKey(booking.checkIn);
  const checkOutDay = bookingDayKey(booking.checkOut);
  state.checkInDay = checkInDay;
  state.checkOutDay = checkOutDay;
  state.preArrival = !!checkInDay && !!today && today < checkInDay;
  state.stayEnded = !!checkOutDay && !!today && today > checkOutDay;
  state.stayOver = stayOverAt(checkOutDay, checkoutTime, now);

  const nights = checkInDay && checkOutDay ? dayKeyDiff(checkOutDay, checkInDay) : null;
  const timing = today && checkInDay && checkOutDay && nights !== null && nights > 0
    ? stayTiming({ checkIn: checkInDay, checkOut: checkOutDay }, localMidnight(today))
    : null;

  if (!timing || nights === null) {
    // Date mancanti o senza notti: la S di sempre, senza contatori. Dopo la
    // partenza (se la data c'è) comunque A: la casa non si apre più.
    state.view = state.stayEnded ? 'after' : 'stay';
    state.doorCodeAvailable = checkInDone && !state.stayOver;
    state.homeAvailable = checkInDone && !state.preArrival && !state.stayOver;
    return state;
  }

  const { phase, daysToCheckIn, daysToCheckOut } = timing;
  state.phase = phase;
  state.daysToCheckIn = daysToCheckIn;
  state.daysToCheckOut = daysToCheckOut;
  state.totalNights = nights;
  state.isArrivalDay = phase === 'arrival';
  state.pastCheckInTime = state.isArrivalDay && romeMinutes(now) >= (timeMinutes(checkInTime) ?? 15 * 60);
  // Come la vecchia Home: la vigilia è un giorno "di soggiorno". In un
  // soggiorno di una notte la vigilia è il giorno dell'arrivo, e vince l'arrivo.
  state.isEve = phase === 'stay' && daysToCheckOut === 1;
  state.isCheckoutDay = phase === 'checkout';
  state.night = phase === 'arrival' || phase === 'stay' ? 1 - daysToCheckIn : null;

  const { CHECKIN_AVAILABLE_DAYS_BEFORE: daysBefore, CHECKIN_AVAILABLE_DAYS_AFTER: daysAfter } = CHECKIN_CONFIG;
  state.checkInWindowOpen = (daysToCheckIn <= daysBefore && daysToCheckIn >= -daysAfter)
    // Da rifare: fino alla partenza (il channel manager lo permette).
    || (!!state.checkInRedo && daysToCheckIn <= daysBefore && daysToCheckOut >= 0);
  state.checkInOpensOn = daysToCheckIn > daysBefore && checkInDay ? shiftDayKey(checkInDay, -daysBefore) : null;

  if (phase === 'far' || phase === 'soon') state.view = 'before';
  else if (phase === 'arrival') state.view = state.pastCheckInTime ? 'stay' : 'before';
  else if (phase === 'stay' || phase === 'checkout') state.view = 'stay';
  else state.view = 'after';

  // Il codice si vede già prima dell'arrivo (come in MyStay), mai dopo
  // l'orario di check-out (più l'ora di margine); WiFi e casa solo dal giorno
  // di arrivo.
  state.doorCodeAvailable = checkInDone && state.view !== 'after' && !state.stayOver;
  state.homeAvailable = checkInDone && !state.preArrival && !state.stayOver;
  return state;
}

export type StayDayLabel =
  | { key: 'home.stay.nightOf'; night: number; total: number }
  | { key: 'home.stay.lastNight' }
  | { key: 'home.stay.oneNight' }
  | { key: 'home.stay.checkoutDay' }
  | null;

/**
 * La riga dell'intestazione, a notti (lib/stayNight): "Notte 3 di 7", la
 * vigilia "Ultima notte", con una notte sola "Una notte con noi · partenza
 * domani", il giorno della partenza "Giorno della partenza" (mai "Notte 7
 * di 7" né "Giorno 8 di 8"). null prima dell'arrivo, dopo la partenza e
 * senza date o senza notti.
 */
export function stayDayLabel(state: Pick<HomeState, 'checkInDay' | 'checkOutDay' | 'today'>): StayDayLabel {
  const n = stayNightOf(state.checkInDay, state.checkOutDay, state.today);
  if (!n) return null;
  switch (n.kind) {
    case 'night':
      return { key: 'home.stay.nightOf', night: n.night, total: n.total };
    case 'lastNight':
      return { key: 'home.stay.lastNight' };
    case 'oneNight':
      return { key: 'home.stay.oneNight' };
    case 'departure':
    default:
      return { key: 'home.stay.checkoutDay' };
  }
}
